import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { meetingMediaUrl } from "../../api/Meetings/meetingSessionsApi";
import { mediaKindFromMime } from "../../utils/meetingRecorder";
import MeetingVideoThumb from "./MeetingVideoThumb";

function isVideoPhoto(photo) {
  return (
    photo?.kind === "video" || mediaKindFromMime(photo?.mimeType) === "video"
  );
}

/**
 * Galería con miniaturas grandes.
 * Foto: clic → ir a pastilla.
 * Vídeo: clic / ▶ → reproducir aquí; doble clic → ir a pastilla.
 */
export default function MeetingMediaGalleryDialog({
  open,
  onClose,
  photos = [],
  initialPhotoId = null,
  onJumpToPhoto,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const scrollRef = useRef(null);
  const [playingId, setPlayingId] = useState(null);
  const items = useMemo(
    () =>
      Array.isArray(photos) ? photos.filter((p) => p && !p.pendingUpload) : [],
    [photos],
  );

  useEffect(() => {
    if (!open) setPlayingId(null);
  }, [open]);

  useEffect(() => {
    if (!open || initialPhotoId == null || playingId != null) return undefined;
    const id = Number(initialPhotoId);
    const timer = window.setTimeout(() => {
      const el = scrollRef.current?.querySelector?.(
        `[data-gallery-photo-id="${id}"]`,
      );
      el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [open, initialPhotoId, items.length, playingId]);

  const jump = (photoId) => {
    onJumpToPhoto?.(Number(photoId));
  };

  const playing = useMemo(
    () => items.find((p) => Number(p.id) === Number(playingId)) || null,
    [items, playingId],
  );

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (playingId != null) {
          setPlayingId(null);
          return;
        }
        onClose?.();
      }}
      fullWidth
      fullScreen={isNarrow}
      maxWidth="lg"
      className="meeting-no-print"
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.5)",
          },
        },
      }}
      BackdropProps={{
        sx: {
          backdropFilter: "blur(10px)",
          backgroundColor: "rgba(15, 23, 42, 0.5)",
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: isNarrow ? 0 : 3,
          overflow: "hidden",
          maxHeight: "92vh",
          minHeight: { md: "78vh" },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          pr: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
            {playing
              ? `${t("Meetings.video")} #${playing.id}`
              : t("Meetings.mediaGalleryDialogTitle")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {playing
              ? t("Meetings.mediaGalleryVideoPlayingHint")
              : t("Meetings.mediaGalleryDialogHint", { count: items.length })}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.75} alignItems="center">
          {playing ? (
            <>
              <Button
                size="small"
                variant="contained"
                startIcon={<DriveFileMoveOutlinedIcon />}
                onClick={() => jump(playing.id)}
                sx={{ fontWeight: 700, textTransform: "none" }}
              >
                {t("Meetings.mediaGalleryGoToItem")}
              </Button>
              <Chip
                size="small"
                label={t("Meetings.mediaGalleryBackToGrid")}
                onClick={() => setPlayingId(null)}
                sx={{ fontWeight: 700, cursor: "pointer" }}
              />
            </>
          ) : null}
          <IconButton
            aria-label={t("Meetings.cancel")}
            onClick={() => {
              if (playingId != null) setPlayingId(null);
              else onClose?.();
            }}
            size="small"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        ref={scrollRef}
        sx={{
          p: 0,
          bgcolor:
            playing != null
              ? alpha("#000", 0.92)
              : alpha(theme.palette.action.hover, 0.25),
          minHeight: { xs: "72dvh", md: "70vh" },
          overflow: playing != null ? "hidden" : "auto",
        }}
      >
        {playing ? (
          <Box
            sx={{
              minHeight: { xs: "72dvh", md: "70vh" },
              display: "grid",
              placeItems: "center",
              px: { xs: 1.5, sm: 3 },
              py: 2,
            }}
          >
            <Box
              component="video"
              key={playing.id}
              src={meetingMediaUrl(playing.fileUrl)}
              controls
              autoPlay
              playsInline
              sx={{
                width: "100%",
                maxHeight: { xs: "68dvh", md: "78vh" },
                bgcolor: "black",
                borderRadius: 1.5,
                display: "block",
              }}
            />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
            <Typography color="text.secondary">
              {t("Meetings.mediaGalleryDialogEmpty")}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              p: { xs: 1.5, sm: 2, md: 2.5 },
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
              },
              gap: { xs: 1.25, sm: 1.5, md: 2 },
              alignContent: "start",
            }}
          >
            {items.map((photo) => {
              const video = isVideoPhoto(photo);
              const highlighted =
                initialPhotoId != null &&
                Number(initialPhotoId) === Number(photo.id);
              return (
                <Box
                  key={photo.id}
                  data-gallery-photo-id={photo.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (video) setPlayingId(Number(photo.id));
                    else jump(photo.id);
                  }}
                  onDoubleClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    jump(photo.id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      if (video) setPlayingId(Number(photo.id));
                      else jump(photo.id);
                    }
                  }}
                  sx={{
                    position: "relative",
                    borderRadius: 2,
                    overflow: "hidden",
                    cursor: "pointer",
                    zIndex: highlighted ? 2 : 1,
                    transition:
                      "box-shadow 160ms ease, border-color 160ms ease",
                    "&:hover .meeting-gallery-thumb": {
                      boxShadow: `0 10px 28px ${alpha("#0f172a", 0.2)}`,
                      borderColor: alpha(theme.palette.primary.main, 0.45),
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${theme.palette.primary.main}`,
                      outlineOffset: 3,
                    },
                  }}
                >
                  <Box
                    className="meeting-gallery-thumb"
                    sx={{
                      borderRadius: 2,
                      overflow: "hidden",
                      border: "2px solid",
                      borderColor: highlighted
                        ? alpha(theme.palette.primary.main, 0.7)
                        : "divider",
                      bgcolor: "#f3f3f3",
                      boxShadow: `0 6px 18px ${alpha("#0f172a", 0.12)}`,
                      aspectRatio: "3 / 4",
                      transition:
                        "box-shadow 160ms ease, border-color 160ms ease",
                    }}
                  >
                    {video ? (
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        <MeetingVideoThumb
                          fileUrl={photo.fileUrl}
                          eager
                          sx={{
                            width: "100%",
                            height: "100%",
                            aspectRatio: "unset",
                            borderRadius: 0,
                          }}
                        />
                        <IconButton
                          size="small"
                          aria-label={t("Meetings.mediaGalleryPlayVideo")}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPlayingId(Number(photo.id));
                          }}
                          sx={{
                            position: "absolute",
                            right: 6,
                            bottom: 6,
                            zIndex: 2,
                            bgcolor: alpha("#000", 0.55),
                            color: "#fff",
                            "&:hover": { bgcolor: alpha("#000", 0.75) },
                          }}
                        >
                          <PlayArrowRoundedIcon />
                        </IconButton>
                        <PlayCircleOutlineIcon
                          sx={{
                            position: "absolute",
                            left: "50%",
                            top: "50%",
                            transform: "translate(-50%, -50%)",
                            color: alpha("#fff", 0.92),
                            filter: "drop-shadow(0 1px 4px rgba(0,0,0,.55))",
                            fontSize: 48,
                            pointerEvents: "none",
                          }}
                        />
                      </Box>
                    ) : (
                      <Box
                        component="img"
                        src={meetingMediaUrl(photo.fileUrl)}
                        alt={photo.caption || `#${photo.id}`}
                        loading="lazy"
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          display: "block",
                          bgcolor: "#f3f3f3",
                        }}
                      />
                    )}
                  </Box>
                  <Chip
                    size="small"
                    label={`#${photo.id}`}
                    sx={{
                      position: "absolute",
                      top: 8,
                      left: 8,
                      height: 22,
                      fontWeight: 800,
                      bgcolor: alpha("#000", 0.72),
                      color: "#fff",
                      pointerEvents: "none",
                      "& .MuiChip-label": { px: 0.7 },
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
